import { Box, FormControl, FormLabel, Theme, Typography, useMediaQuery } from "@mui/material";
import CabinetPage from "../../components/Common/CabinetPage";
import {
  GridEventListener,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRowModel,
  GridRowSelectionModel,
  GridSortModel,
  getGridDateOperators,
  getGridStringOperators,
  getGridSingleSelectOperators
} from "@mui/x-data-grid-pro";
import { useCallback, useMemo, useState } from "react";
import { Leader } from "../../store";
import { 
  ContactRequest, 
  ContactType, 
  LeaderRelationship, 
  ContactCadenceRequest, 
  ContactCadence,
  Tag,
  RelationshipTag
} from "../../store/cabinetApi/generated/crm";
import { DateTime } from "luxon";
import CRMTable, { CRMGridColDef } from '../../components/CRM/CRMTable';
import { selectFilterOperator } from '../../components/DataGrid/utils';
import { AbstractEventAnalyticsLabel } from '../../store';
import { CabExecPicker } from "@CabComponents/CabExecPicker";
import CabAvatar from "@CabComponents/CabAvatar";
import { getLeaderIconSrc } from "../../utils/leaderUtils";
import NewContactModal from "../CRMContacts/NewContactModal";
import { CabButton } from "@CabComponents/CabButton";
import { isLargeFormatDevice } from "../../utils/screenSizeUtils";
import SearchInputForm, { SearchForm } from "../../components/CRM/SearchInputForm";
import { CabTooltip } from "@CabComponents/CabTooltip";
import { CabIcon } from "@CabComponents/CabIcon";
import { IoFlashOff, IoPricetagOutline, IoLinkOutline, IoEyeOffOutline } from "react-icons/io5";
import colors from "../../colors";
import { CabToggleChip } from "@CabComponents/CabToggleChip";
import { CabModal } from "@CabComponents/CabModal";
import CadenceEditModal from "./CadenceEditModal";
import { BulkAddTagsModal } from "./BulkAddTagsModal";
import { getSelectedRowCount } from "../../components/DataGrid/rowSelection";


type CrmPreferencesForRelationships = Array<{
  id: number;
  relationship_list_column_order: string[];
  relationship_list_hidden_columns?: string[];
}>;

const CRMRelationships = ({
  relationships, page, pageSize, rowCount, onPageChange, onNavigateToContact, onCreateContact, filter, 
  onSetFilter, sort, onSetSort, loadingRelationships, onLeaderSelected, selectedLeaderId, leaderOptions,
  contactTags, relationshipTags, contactTypes, isCreatingContact, onSearch, onUpdateRelationship, onSaveCadence,
  rowSelectionModel, onRowSelectionModelChange, onBulkAddContactTags, onBulkAddRelationshipTags, onBulkHideContacts,
  crmPreferencesData, onUpdateCrmPreferences
}: {
  relationships: LeaderRelationship[];
  contactTags: Tag[];
  relationshipTags: RelationshipTag[];
  contactTypes: ContactType[];
  isCreatingContact: boolean;
  page: number;
  pageSize: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  onNavigateToContact: (id: LeaderRelationship['id']) => void;
  onCreateContact: (contact: ContactRequest, relationshipLeaders?: Leader['id'][]) => Promise<void>;
  filter?: GridFilterModel;
  onSetFilter: (filter: GridFilterModel | undefined) => void;
  sort?: GridSortModel;
  onSetSort: (sort: GridSortModel | undefined) => void;
  loadingRelationships: boolean;
  selectedLeaderId?: Leader['id'];
  onLeaderSelected: (leaderId: Leader['id']) => void;
  leaderOptions: Leader[];
  onSearch: (search: string) => void;
  onUpdateRelationship: (
    contact: Partial<LeaderRelationship> & { id: LeaderRelationship['id'] }
  ) => Promise<LeaderRelationship | undefined>;
  onSaveCadence: (cadenceData: ContactCadenceRequest & { id?: number }) => Promise<unknown>;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  onBulkAddContactTags?: (tagIds: number[]) => void;
  onBulkAddRelationshipTags?: (tagIds: number[]) => void;
  onBulkHideContacts?: () => void;
  crmPreferencesData?: CrmPreferencesForRelationships;
  onUpdateCrmPreferences?: (args: {
    id: number;
    patchedCrmPreferences: {
      relationship_list_column_order?: string[];
      relationship_list_hidden_columns?: string[];
    };
  }) => void;
}) => {
  const [openNewContactModal, setOpenNewContactModal] = useState(false);
  const [openCadenceModal, setOpenCadenceModal] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState<LeaderRelationship | null>(null);
  const isSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const cabExecOptions = useMemo(() => leaderOptions.map(leader => {
    return {
      value: leader.id,
      label: `${leader.first_name} ${leader.last_name}`,
      icon: <CabAvatar
        src={getLeaderIconSrc(leader)}
        color={leader.color}
        name={`${leader.first_name}
        ${leader.last_name}`}
        size="small"
      />
    };
  }), [leaderOptions]);

  const handleSearch = ({ search }: SearchForm) => {
    onSearch(search);
  };

  const handleOpenCadenceModal = (relationship: LeaderRelationship) => {
    setSelectedRelationship(relationship);
    setOpenCadenceModal(true);
  };

  const handleCloseCadenceModal = () => {
    setOpenCadenceModal(false);
    setSelectedRelationship(null);
  };

  const handleSaveCadenceLocal = async (cadenceData: ContactCadenceRequest & { id?: number }) => {
    await onSaveCadence(cadenceData);
  };

  return (
    <CabinetPage
      pageName="Relationships"
      headerContent={
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
          <Typography variant="h1">
            Relationships
          </Typography>
          {!isSmDown && (
            <SearchInputForm
              onSearch={handleSearch}
              sx={{ flex: 1, maxWidth: 500, paddingLeft: 4, paddingRight: 4 }}
            />
          )}
          {isLargeFormatDevice() && (
            <CabButton
              onClick={() => setOpenNewContactModal(true)}
            >
              New Person
            </CabButton>
          )}
        </Box>
      }
    >
      <>
        <Box
          sx={{
            padding: 2,
            display: "flex",
            flexDirection: "column",
            flex: 1
          }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between"
            }}>
            <FormControl>
              <FormLabel sx={{ fontWeight: 'bold' }}>Show Relationships with</FormLabel>
              <CabExecPicker
                value={selectedLeaderId || -1}
                options={cabExecOptions}
                onChange={onLeaderSelected}
                placeholder="Select a Teammate"
                sx={{ width: 300, marginTop: '0px !important' }}
              />
            </FormControl>
            {isSmDown && (
              <SearchInputForm onSearch={handleSearch} />
            )}
          </Box>

          <Box
            sx={{
              position: "relative",
              height: "100%"
            }}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0
              }}>
              <RelationshipTable
                relationships={relationships}
                page={page}
                pageSize={pageSize}
                rowCount={rowCount}
                onPageChange={onPageChange}
                onNavigateToRelationship={onNavigateToContact}
                filter={filter}
                onSetFilter={onSetFilter}
                sort={sort}
                onSetSort={onSetSort}
                defaultHiddenFields={['title', 'relationship_strength_auto', 'contact_created_at']}
                loading={loadingRelationships}
                onUpdateRelationship={onUpdateRelationship}
                onOpenCadenceModal={handleOpenCadenceModal}
                contactTags={contactTags}
                relationshipTags={relationshipTags}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={onRowSelectionModelChange}
                onBulkAddContactTags={onBulkAddContactTags}
                onBulkAddRelationshipTags={onBulkAddRelationshipTags}
                onBulkHideContacts={onBulkHideContacts}
                crmPreferencesData={crmPreferencesData}
                onUpdateCrmPreferences={onUpdateCrmPreferences}
              />
            </Box>
          </Box>
        </Box>

        <NewContactModal
          open={openNewContactModal}
          onClose={() => setOpenNewContactModal(false)}
          contactTags={contactTags}
          contactTypes={contactTypes}
          onCreateContact={onCreateContact}
          isCreatingContact={isCreatingContact}
          leaderOptions={leaderOptions}
        />

        {selectedRelationship && (
          <CadenceEditModal
            open={openCadenceModal}
            onClose={handleCloseCadenceModal}
            cadence={selectedRelationship.contact_cadence}
            contactId={selectedRelationship.contact.id}
            teammateId={selectedRelationship.leader.id}
            onSaveCadence={handleSaveCadenceLocal}
          />
        )}
      </>
    </CabinetPage>
  );
};

const relationshipStrengthNumToStr = (value: number) => {
  if (value >= 6) return 'strong';
  if (value >= 3) return 'good';
  if (value >= 0) return 'weak';
  return 'auto';
};

const parseDurationFormat = (durationString: string): { amount: number; duration: string } => {
  // Handle duration format like "7 00:00:00" or "30 00:00:00"
  const parts = durationString.split(' ');
  if (parts.length >= 1) {
    const days = parseInt(parts[0]);
    if (!isNaN(days)) {
      if (days % 365 === 0 && days >= 365) {
        return { amount: days / 365, duration: 'years' };
      } else if (days % 30 === 0 && days >= 30) {
        return { amount: days / 30, duration: 'months' };
      } else if (days % 7 === 0 && days >= 7) {
        return { amount: days / 7, duration: 'weeks' };
      } else {
        return { amount: days, duration: 'days' };
      }
    }
  }
  return { amount: 1, duration: 'days' };
};

// Cadence status color constants
const CADENCE_STATUS_COLORS = {
  success: { color: 'success' as const, backgroundColor: colors.forest300 },
  warning: { color: 'warning' as const, backgroundColor: colors.chamomile200 },
  error: { color: 'error' as const, backgroundColor: colors.sunset300 },
  default: { color: 'default' as const, backgroundColor: 'transparent' },
};

const getCadenceStatusDisplay = (cadenceStatus: string, cadenceDueDate: string | null, nextMeetingDate?: string) => {
  if (!cadenceDueDate) {
    return { status: 'Not Set', ...CADENCE_STATUS_COLORS.default };
  }

  const now = DateTime.now();
  const dueDate = DateTime.fromISO(cadenceDueDate);
  
  // Handle meeting scheduled cases - both show as green (success)
  if (cadenceStatus === 'meeting_scheduled' || cadenceStatus === 'meeting_scheduled_late') {
    if (nextMeetingDate) {
      const nextMeeting = DateTime.fromISO(nextMeetingDate);
      const daysUntilMeeting = Math.ceil(nextMeeting.diff(now, 'days').days);
      
      let statusText: string;
      if (daysUntilMeeting > 56) {
        // More than 8 weeks (56 days) - use months
        const monthsUntilMeeting = Math.round(daysUntilMeeting / 30);
        statusText = `${monthsUntilMeeting} month${monthsUntilMeeting === 1 ? '' : 's'} until meeting`;
      } else if (daysUntilMeeting > 14) {
        // More than 14 days - use weeks
        const weeksUntilMeeting = Math.round(daysUntilMeeting / 7);
        statusText = `${weeksUntilMeeting} week${weeksUntilMeeting === 1 ? '' : 's'} until meeting`;
      } else {
        // 14 days or less - use days
        statusText = `${daysUntilMeeting} day${daysUntilMeeting === 1 ? '' : 's'} until meeting`;
      }
      
      return {
        status: statusText,
        ...CADENCE_STATUS_COLORS.success
      };
    }
    return {
      status: 'Meeting scheduled',
      ...CADENCE_STATUS_COLORS.success
    };
  }
  
  // Handle other statuses
  const daysUntilDue = Math.ceil(dueDate.diff(now, 'days').days);
  
  switch (cadenceStatus) {
    case 'not_set':
      return { status: 'Not Set', ...CADENCE_STATUS_COLORS.default };
    case 'on_track':
      return { 
        status: `${daysUntilDue} days to schedule`, 
        ...CADENCE_STATUS_COLORS.success
      };
    case 'due_soon':
      return { 
        status: `${daysUntilDue} days to schedule`, 
        ...CADENCE_STATUS_COLORS.warning
      };
    case 'overdue':
      return { 
        // status: `${Math.abs(daysUntilDue)} days past due`, 
        status: 'Not scheduled', 
        ...CADENCE_STATUS_COLORS.error
      };
    default:
      return { status: 'Unknown', ...CADENCE_STATUS_COLORS.default };
  }
};

const createRelationshipColumns = (
  contactTags: Tag[],
  relationshipTags: RelationshipTag[]
): CRMGridColDef<LeaderRelationship>[] => {
  // Create lookup maps for tags
  const contactTagsMap = Object.fromEntries(contactTags.map(tag => [tag.id, tag]));
  const relationshipTagsMap = Object.fromEntries(relationshipTags.map(tag => [tag.id, tag]));

  // Create options and rawOptions for contact tags filter
  const contactTagOptions = contactTags.map(tag => ({
    value: tag.id,
    label: tag.name
  }));
  const contactTagsRawOptions = Object.fromEntries(
    contactTags.map(tag => [String(tag.id), tag])
  ) as Record<string, AbstractEventAnalyticsLabel>;

  // Create options and rawOptions for relationship tags filter
  const relationshipTagOptions = relationshipTags.map(tag => ({
    value: tag.id,
    label: tag.name
  }));
  const relationshipTagsRawOptions = Object.fromEntries(
    relationshipTags.map(tag => [String(tag.id), tag])
  );

  return [
    {
      field: 'name',
      headerName: 'Name',
      type: 'string',
      width: 200,
      display: "flex",
      filterOperators: getGridStringOperators().filter(op => !['isAnyOf', 'isEmpty', 'isNotEmpty'].includes(op.value)),
      valueGetter: (value, row, column, apiRef) => row.contact?.name || '',
    },
    {
      field: 'title',
      headerName: 'Title',
      type: 'string',
      width: 200,
      display: "flex",
      valueGetter: (value, row, column, apiRef) => row.contact?.title || '',
    },
    {
      field: 'primary_email',
      headerName: 'Email',
      type: 'string',
      width: 200,
      display: "flex",
      valueGetter: (value, row, column, apiRef) => row.contact?.primary_email || '',
    },
    {
      field: 'prev_meeting_data',
      headerName: 'Last Meeting',
      type: 'dateTime',
      width: 150,
      display: "flex",
      filterOperators: getGridDateOperators(false).filter(op => ['onOrAfter', 'onOrBefore'].includes(op.value)),
      valueGetter: (value, row, column, apiRef) => (
        row.prev_meeting_data?.['v1_15']
          ? DateTime.fromISO(row.prev_meeting_data['v1_15'].start_date).toJSDate()
          : undefined
      ),
      valueFormatter: (val?: Date) => val && DateTime.fromJSDate(val).toLocaleString({
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
        timeZoneName: 'short',
      }),
      renderCell: (params) => {
        const tooltip = params.row.prev_meeting_data?.['v1_15']
          ? `${params.row.prev_meeting_data['v1_15'].title}\n${params.formattedValue}`
          : '';
        const dateOnly = params.value ? DateTime.fromJSDate(params.value).toLocaleString(DateTime.DATE_MED) : '';
        return (
          <CabTooltip title={tooltip} wrapWithSpan>
            {dateOnly}
          </CabTooltip>
        );
      }
    },
    {
      field: 'next_meeting_data',
      headerName: 'Next Meeting',
      type: 'dateTime',
      width: 150,
      display: "flex",
      filterOperators: getGridDateOperators(false).filter(op => ['onOrAfter', 'onOrBefore'].includes(op.value)),
      valueGetter: (value, row, column, apiRef) => (
        row.next_meeting_data?.['v1_15']
          ? DateTime.fromISO(row.next_meeting_data['v1_15'].start_date).toJSDate()
          : undefined
      ),
      valueFormatter: (val?: Date) => val && DateTime.fromJSDate(val).toLocaleString({
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
        timeZoneName: 'short',
      }),
      renderCell: (params) => {
        const tooltip = params.row.next_meeting_data?.['v1_15']
          ? `${params.row.next_meeting_data['v1_15'].title}\n${params.formattedValue}`
          : '';
        const dateOnly = params.value ? DateTime.fromJSDate(params.value).toLocaleString(DateTime.DATE_MED) : '';
        return (
          <CabTooltip title={tooltip} wrapWithSpan>
            {dateOnly}
          </CabTooltip>
        );
      }
    },
    {
      field: 'relationship_strength',
      headerName: 'Relationship Strength',
      width: 160,
      display: "flex",
      type: 'singleSelect',
      chipSelect: true,
      editable: true,
      valueOptions: [
        { value: 'auto', label: 'Auto', color: colors.black300 },
        { value: 'weak', label: 'Weak', color: '#FCF55F', },
        { value: 'good', label: 'Good', color: colors.blueLight },
        { value: 'strong', label: 'Strong', color: colors.greenPrimary },
      ],
      // filterOperators: getGridSingleSelectOperators().filter(op => op.value !== 'not'),
      valueGetter: (value, row, column, apiRef) => {
        return relationshipStrengthNumToStr(Number(row.relationship_strength));
      },
      valueSetter: (value, row) => {
        return {
          ...row,
          relationship_strength_override: value === 'auto' ? null : value,
        };
      },
    },
    {
      field: 'relationship_strength_override',
      headerName: 'Relationship Strength Override',
      width: 30,
      display: "flex",
      type: 'boolean',
      valueGetter: (value, row, column, apiRef) => value != null,
      renderCell: ({ value }) => (
        value && (
          <CabTooltip
            title="This relationship's strength has been set manually. Cabinet will not automatically update it."
            wrapWithSpan
          >
            <CabIcon Icon={IoFlashOff} />
          </CabTooltip>
        )
      ),
      renderHeader: () => '',
    // filterable: false,
    // sortable: false,
    },
    // {
    //   field: 'relationship_strength_auto',
    //   headerName: 'Auto RS',
    //   width: 100,
    //   display: "flex",
    //   type: 'singleSelect',
    //   filterable: false,
    //   sortable: false,
    // },
    {
      field: 'contact_created_at',
      headerName: 'Created On',
      type: 'dateTime',
      width: 200,
      display: "flex",
      filterOperators: getGridDateOperators(false).filter(op => ['onOrAfter', 'onOrBefore'].includes(op.value)),
      valueGetter: (value, row, column, apiRef) => (
        row.contact?.created_at ? DateTime.fromISO(row.contact.created_at).toJSDate() : undefined
      ),
      valueFormatter: (value, row, column, apiRef) => (
        value ? DateTime.fromJSDate(value).toLocaleString(DateTime.DATE_MED) : ''
      ),
    },
    {
      field: 'contact_cadence',
      headerName: 'Cadence',
      type: 'singleSelect',
      width: 200,
      display: "flex",
      valueOptions: [
        { value: 'not_set', label: 'Not set' },
        { value: 'fixed_duration', label: 'Fixed Duration' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'bi_weekly', label: 'Bi-Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'quarterly', label: 'Quarterly' },
        { value: 'annually', label: 'Annually' },
      ],
      filterOperators: getGridSingleSelectOperators().filter(op => op.value === 'is'),
      valueGetter: (cadence: ContactCadence, row, column, apiRef) => {
        if (!cadence) return 'not_set';
      
        // Map frequency type to filter values
        if (cadence.frequency_type !== 'fixed_duration') {
          return cadence.frequency_type; // quarterly, annually
        }
      
        // Parse meeting frequency for fixed_duration
        if (cadence.frequency_type === 'fixed_duration' && cadence.meeting_frequency) {
          const parsed = parseDurationFormat(cadence.meeting_frequency);
        
          // Map common intervals to filter values
          if (parsed.amount === 1 && parsed.duration === 'weeks') {
            return 'weekly';
          } else if (parsed.amount === 1 && parsed.duration === 'months') {
            return 'monthly';
          }
        }
      
        // Default to not_set for unmapped values
        return 'not_set';
      },
      valueFormatter: (value) => {
        const option = [
          { value: 'not_set', label: 'Not set' },
          { value: 'fixed_duration', label: 'Fixed Duration' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'bi_weekly', label: 'Bi-Weekly' },
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'annually', label: 'Annually' },
        ].find(opt => opt.value === value);
        return option?.label || value;
      },
      renderCell: (params) => {
        const cadence = params.row.contact_cadence;
        const hasCadence = cadence !== null;
        
        // Generate display text
        let displayText = '';
        if (!cadence) {
          displayText = 'Not set';
        } else {
          // Format frequency type display
          if (cadence.frequency_type !== 'fixed_duration') {
            displayText = cadence.frequency_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
          }
        
          // Parse meeting frequency for fixed_duration
          if (cadence.frequency_type === 'fixed_duration' && cadence.meeting_frequency) {
            const parsed = parseDurationFormat(cadence.meeting_frequency);
          
            // Special cases for common intervals
            if (parsed.amount === 1 && parsed.duration === 'weeks') {
              displayText = 'Weekly';
            } else if (parsed.amount === 1 && parsed.duration === 'months') {
              displayText = 'Monthly';
            } else {
              const duration = parsed.amount === 1 ? parsed.duration.slice(0, -1) : parsed.duration;
              displayText = `Every ${parsed.amount} ${duration}`;
            }
          }
        
          // Parse advance notice
          if (cadence.advance_notice) {
            const parsed = parseDurationFormat(cadence.advance_notice);
            const duration = parsed.amount === 1 ? parsed.duration.slice(0, -1) : parsed.duration;
            displayText += ` (${parsed.amount} ${duration} notice)`;
          }
        }
        
        return (
          <CabTooltip title="Set recurring reminders to schedule meetings with this contact" wrapWithSpan>
            <Box 
              sx={{ 
                cursor: 'pointer', 
                color: hasCadence ? 'primary.main' : 'text.secondary',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {displayText}
            </Box>
          </CabTooltip>
        );
      },
    },
    {
      field: 'cadence_status',
      headerName: 'Cadence Status',
      type: 'singleSelect',
      width: 170,
      display: "flex",
      valueOptions: [
        { value: 'not_set', label: 'Not Set' },
        { value: 'on_track', label: 'On Track' },
        { value: 'due_soon', label: 'Due Soon' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'meeting_scheduled', label: 'Meeting Scheduled' },
        { value: 'meeting_scheduled_late', label: 'Meeting Scheduled Late' },
      ],
      filterOperators: getGridSingleSelectOperators().filter(op => op.value === 'is'),
      valueGetter: (value, row, column, apiRef) => {
        // Return the raw cadence_status value for filtering
        return row.cadence_status || 'not_set';
      },
      renderCell: (params) => {
        const row = params.row;
        // Generate status display info in renderCell
        const statusInfo = !row.contact_cadence 
          ? { status: 'Not Set', color: 'default' as const, backgroundColor: 'transparent' }
          : getCadenceStatusDisplay(
            row.cadence_status, 
            row.cadence_due_date, 
            row.next_meeting_data?.['v1_15']?.start_date
          );
      
        return (
          <CabTooltip title="Set recurring reminders to schedule meetings with this contact" wrapWithSpan>
            <Box 
              sx={{ 
                cursor: 'pointer', 
                color: statusInfo.color === 'default' ? 'text.secondary' : 'text.primary',
                backgroundColor: statusInfo.backgroundColor,
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {statusInfo.status}
            </Box>
          </CabTooltip>
        );
      },
    },
    {
      field: 'contact_tags',
      headerName: 'Contact Tags',
      type: 'string',
      width: 200,
      display: "flex",
      filterOperators: selectFilterOperator([
        {
          options: contactTagOptions,
          rawOptions: contactTagsRawOptions
        }
      ]),
      valueGetter: (value, row, column, apiRef) => row.contact?.tags || [],
      valueFormatter: (value) => {
        const tagIds = (value as number[]) || [];
        const selectedTags = tagIds
          .map(tagId => contactTagsMap[tagId])
          .filter((tag): tag is Tag => !!tag)
          .map(tag => tag.name)
          .sort()
          .join(', ');
        return selectedTags || '';
      },
      renderCell: (params) => {
        const tagIds = params.row.contact?.tags || [];
        const selectedTags = tagIds
          .map(tagId => contactTagsMap[tagId])
          .filter((tag): tag is Tag => !!tag);
      
        if (selectedTags.length === 0) {
          return (
            <CabToggleChip 
              chipColor={colors.white800} 
              label="None" 
              selected={true} 
            />
          );
        }
      
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5
            }}>
            {selectedTags.map(tag => (
              <CabToggleChip 
                key={tag.id}
                chipColor={tag.color} 
                label={tag.name} 
                selected={true} 
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'tags',
      headerName: 'Relationship Tags',
      type: 'string',
      width: 200,
      display: "flex",
      filterOperators: selectFilterOperator([
        {
          options: relationshipTagOptions,
          rawOptions: relationshipTagsRawOptions as unknown as Record<string, AbstractEventAnalyticsLabel>
        }
      ]),
      valueGetter: (value, row, column, apiRef) => row.tags || [],
      valueFormatter: (value) => {
        const tagIds = (value as number[]) || [];
        const selectedTags = tagIds
          .map(tagId => relationshipTagsMap[tagId])
          .filter((tag): tag is RelationshipTag => !!tag)
          .map(tag => tag.name)
          .sort()
          .join(', ');
        return selectedTags || '';
      },
      renderCell: (params) => {
        const tagIds = params.row.tags || [];
        const selectedTags = tagIds
          .map(tagId => relationshipTagsMap[tagId])
          .filter((tag): tag is RelationshipTag => !!tag);
      
        if (selectedTags.length === 0) {
          return (
            <CabToggleChip 
              chipColor={colors.white800} 
              label="None" 
              selected={true} 
            />
          );
        }
      
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5
            }}>
            {selectedTags.map(tag => (
              <CabToggleChip 
                key={tag.id}
                chipColor={tag.color || colors.white800} 
                label={tag.name} 
                selected={true} 
              />
            ))}
          </Box>
        );
      },
    },
  ];
};


export const RelationshipTable = ({
  relationships, onNavigateToRelationship, page, pageSize, rowCount, onPageChange, filter, onSetFilter,
  sort, onSetSort, loading, defaultHiddenFields, onUpdateRelationship, onOpenCadenceModal,
  contactTags, relationshipTags,
  rowSelectionModel, onRowSelectionModelChange, onBulkAddContactTags, onBulkAddRelationshipTags, onBulkHideContacts,
  crmPreferencesData, onUpdateCrmPreferences
}: {
  relationships: LeaderRelationship[];
  page: number;
  pageSize: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  onNavigateToRelationship: (contactId: number, relationshipId: LeaderRelationship['id']) => void;
  filter?: GridFilterModel;
  onSetFilter: (filter: GridFilterModel | undefined) => void;
  sort?: GridSortModel;
  onSetSort: (filter: GridSortModel | undefined) => void;
  defaultHiddenFields?: string[];
  loading: boolean;
  onUpdateRelationship: (
    contact: Partial<LeaderRelationship> & { id: LeaderRelationship['id'] }
  ) => Promise<LeaderRelationship| undefined>;
  onOpenCadenceModal: (relationship: LeaderRelationship) => void;
  contactTags: Tag[];
  relationshipTags: RelationshipTag[];
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  onBulkAddContactTags?: (tagIds: number[]) => void;
  onBulkAddRelationshipTags?: (tagIds: number[]) => void;
  onBulkHideContacts?: () => void;
  crmPreferencesData?: CrmPreferencesForRelationships;
  onUpdateCrmPreferences?: (args: {
    id: number;
    patchedCrmPreferences: {
      relationship_list_column_order?: string[];
      relationship_list_hidden_columns?: string[];
    };
  }) => void;
}) => {
  const [contactTagsModalOpen, setContactTagsModalOpen] = useState(false);
  const [relationshipTagsModalOpen, setRelationshipTagsModalOpen] = useState(false);

  const hasSelection = getSelectedRowCount(rowSelectionModel) > 0;

  const toolbarLeftContent = useMemo(() => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1
      }}>
      <CabButton
        icon={<CabIcon alt="Add contact tags" size="small" Icon={IoPricetagOutline} />}
        onClick={() => setContactTagsModalOpen(true)}
        disabled={!hasSelection || !onBulkAddContactTags}
        buttonType="tertiary"
        sx={{
          cursor: hasSelection && onBulkAddContactTags ? "pointer" : "default",
          fontSize: "13px",
          paddingRight: 1,
          paddingLeft: 1,
        }}
      >
        Tag Selected Contacts
      </CabButton>
      <CabButton
        icon={<CabIcon alt="Add relationship tags" size="small" Icon={IoLinkOutline} />}
        onClick={() => setRelationshipTagsModalOpen(true)}
        disabled={!hasSelection || !onBulkAddRelationshipTags}
        buttonType="tertiary"
        sx={{
          cursor: hasSelection && onBulkAddRelationshipTags ? "pointer" : "default",
          fontSize: "13px",
          paddingRight: 1,
          paddingLeft: 1,
        }}
      >
        Tag Selected Relationships
      </CabButton>
      <CabButton
        icon={<CabIcon alt="Hide contacts" size="small" Icon={IoEyeOffOutline} />}
        onClick={onBulkHideContacts}
        disabled={!hasSelection || !onBulkHideContacts}
        buttonType="tertiary"
        sx={{
          cursor: hasSelection && onBulkHideContacts ? "pointer" : "default",
          fontSize: "13px",
          paddingRight: 1,
          paddingLeft: 1,
        }}
      >
        Hide Contacts
      </CabButton>
    </Box>
  ), [hasSelection, onBulkAddContactTags, onBulkAddRelationshipTags, onBulkHideContacts]);

  const baseColumns = useMemo(
    () => createRelationshipColumns(contactTags, relationshipTags),
    [contactTags, relationshipTags]
  );

  const orderedColumns = useMemo(() => {
    if (!crmPreferencesData?.[0]?.relationship_list_column_order?.length) {
      return baseColumns;
    }
    const savedOrder = crmPreferencesData[0].relationship_list_column_order;
    const currentFieldNames = new Set(baseColumns.map(c => c.field));
    const savedFieldNames = new Set(savedOrder);
    const validSavedFields = savedOrder.filter(field => currentFieldNames.has(field) && field !== '');
    const newFields = baseColumns
      .filter(col => !savedFieldNames.has(col.field) && col.field !== '')
      .map(col => col.field);
    const finalOrder = [...validSavedFields, ...newFields];
    return finalOrder
      .map(fieldName => baseColumns.find(col => col.field === fieldName))
      .filter(Boolean) as typeof baseColumns;
  }, [crmPreferencesData, baseColumns]);

  const columnVisibilityModel = useMemo((): GridColumnVisibilityModel => {
    if (!crmPreferencesData?.[0]?.relationship_list_hidden_columns?.length) {
      return {};
    }
    const hiddenColumns = crmPreferencesData[0].relationship_list_hidden_columns;
    const model: GridColumnVisibilityModel = {};
    hiddenColumns.forEach(field => {
      if (field !== '') {
        model[field] = false;
      }
    });
    return model;
  }, [crmPreferencesData]);

  const handleColumnOrderChange = useCallback<GridEventListener<'columnOrderChange'>>((params, event, details) => {
    if (crmPreferencesData?.[0] && onUpdateCrmPreferences && details.api) {
      const allColumns = details.api.getAllColumns();
      const newOrder = allColumns
        .filter(col => col.field !== '')
        .map(col => col.field);
      onUpdateCrmPreferences({
        id: crmPreferencesData[0].id,
        patchedCrmPreferences: { relationship_list_column_order: newOrder },
      });
    }
  }, [crmPreferencesData, onUpdateCrmPreferences]);

  const handleColumnVisibilityChange = useCallback((model: GridColumnVisibilityModel) => {
    if (crmPreferencesData?.[0] && onUpdateCrmPreferences) {
      const hiddenColumns = Object.entries(model)
        .filter(([field, isVisible]) => !isVisible && field !== '')
        .map(([field]) => field);
      onUpdateCrmPreferences({
        id: crmPreferencesData[0].id,
        patchedCrmPreferences: { relationship_list_hidden_columns: hiddenColumns },
      });
    }
  }, [crmPreferencesData, onUpdateCrmPreferences]);

  const [promiseArguments, setPromiseArguments] = useState<{
    resolve: (value: LeaderRelationship) => void,
    reject: (value: LeaderRelationship) => void,
    originalRow: GridRowModel<LeaderRelationship>;
    updatedRow: GridRowModel<LeaderRelationship>;
  } | null>(null);

  const handleConfirmCancel = () => {
    if (!promiseArguments) return;
    const { originalRow, resolve } = promiseArguments;
    resolve(originalRow); // Resolve with the old row to not update the internal state
    setPromiseArguments(null);
  };

  const handleConfirm = async () => {
    if (!promiseArguments) return;
    const { updatedRow, originalRow, reject, resolve } = promiseArguments;

    try {
      await handleRowUpdate(updatedRow, originalRow);
      resolve(updatedRow);
      setPromiseArguments(null);
    } catch (error) {
      reject(originalRow);
      setPromiseArguments(null);
    }
  };

  const handleCellClick = useCallback<GridEventListener<'cellClick'>>((params, event, details) => {
    if (params.field === '__check__') return;
    if (params.field === 'contact_cadence' || params.field === 'cadence_status') {
      onOpenCadenceModal(params.row);
    } else if (params.field !== 'relationship_strength' && params.field !== 'Options' && params.row.contact) {
      onNavigateToRelationship(params.row.contact.id, params.row.id);
    }
  }, [onNavigateToRelationship, onOpenCadenceModal]);

  const handleRowUpdate = useCallback(async (
    updatedRow: LeaderRelationship, originalRow: LeaderRelationship,
  ) => {
    const res = await onUpdateRelationship(updatedRow);
    return res || originalRow;
  }, [onUpdateRelationship]);

  return (
    <>
      <CRMTable
        columns={orderedColumns}
        rows={relationships}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        onPageChange={onPageChange}
        onCellClick={handleCellClick}
        filter={filter}
        onSetFilter={onSetFilter}
        sort={sort}
        onSetSort={onSetSort}
        loading={loading}
        defaultHiddenFields={defaultHiddenFields}
        onRowUpdate={handleRowUpdate}
        toolbarLeftContent={toolbarLeftContent}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        columnVisibilityModel={columnVisibilityModel}
        dataGridProps={{
          checkboxSelection: true,
          disableRowSelectionOnClick: true,
          rowSelectionModel,
          onRowSelectionModelChange: onRowSelectionModelChange,
          initialState: {
            pinnedColumns: {
              left: ['__check__', 'name']
            }
          },
          processRowUpdate: async (updatedRow, originalRow) => {
            // if useroverride is changed to something other than auto, trigger modal
            if (updatedRow.relationship_strength_override
              !== originalRow.relationship_strength_override
              // and if original row was not already being overridden
              && originalRow.relationship_strength_override == null
              && updatedRow.relationship_strength_override != null
            ) {
              return new Promise((resolve, reject) => {
                // Save the arguments to resolve or reject the promise from modal
                setPromiseArguments({ resolve, reject, updatedRow, originalRow });
              });
            }

            handleRowUpdate(updatedRow, originalRow);
            return updatedRow;
          }
        }}
      />
      {promiseArguments && (
        <CabModal
          open={true}
          onClose={handleConfirmCancel}
          isAlert
          noFullScreen={true}
          text= "Relationship score will no longer be calculated automatically. Are you sure you want to override it?"
          actionButtons={
            <>
              <CabButton buttonType='secondary' onClick={handleConfirmCancel}>
                Cancel
              </CabButton>
              <CabButton onClick={handleConfirm} color="error">
                Yes
              </CabButton>
            </>
          }
        />
      )}
      {onBulkAddContactTags && (
        <BulkAddTagsModal
          open={contactTagsModalOpen}
          onClose={() => setContactTagsModalOpen(false)}
          title="Add Contact tags"
          tagOptions={contactTags}
          onApply={onBulkAddContactTags}
        />
      )}
      {onBulkAddRelationshipTags && (
        <BulkAddTagsModal
          open={relationshipTagsModalOpen}
          onClose={() => setRelationshipTagsModalOpen(false)}
          title="Add Relationship tags"
          tagOptions={relationshipTags}
          onApply={onBulkAddRelationshipTags}
        />
      )}
    </>
  );
};


export default CRMRelationships;
